import React, { useState, useEffect } from 'react';
import BackButton from '@/components/BackButton';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { User, Lock, Eye, EyeSlash, EnvelopeSimple, Phone, FloppyDisk, Camera, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getDeptLabel } from '@/constants/departments';
import EmployeeAvatar from '@/components/EmployeeAvatar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

export default function Profile() {
  const { t } = useTranslation();
  const { direction, language } = useLanguage();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Profile fields
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Password fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Photo upload
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = React.useRef(null);

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(language === 'ar' ? 'الملف يجب أن يكون صورة' : 'File must be an image');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error(language === 'ar' ? 'حجم الصورة يجب ألا يتجاوز 8 ميغابايت' : 'Image must be under 8 MB');
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/me/photo`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUser((u) => ({ ...u, photo_path: data.photo_path, photo_updated_at: data.photo_updated_at }));
      toast.success(language === 'ar' ? 'تم رفع الصورة' : 'Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل رفع الصورة' : 'Upload failed'));
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePhotoDelete = async () => {
    if (!window.confirm(language === 'ar' ? 'حذف الصورة الشخصية؟' : 'Delete profile photo?')) return;
    try {
      await axios.delete(`${API}/me/photo`, { withCredentials: true });
      setUser((u) => ({ ...u, photo_path: null, photo_updated_at: null }));
      toast.success(language === 'ar' ? 'تم حذف الصورة' : 'Photo deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      setEmail(data.email || '');
      setPhone(data.phone || '');
    } catch {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await axios.put(`${API}/auth/profile`, { email, phone }, { withCredentials: true });
      toast.success(t('profile_updated'));
      setUser({ ...user, ...data.user });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('profile_update_failed'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error(t('password_min_length'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('passwords_dont_match'));
      return;
    }
    setSavingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { withCredentials: true });
      toast.success(t('password_changed_success'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('password_change_failed'));
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F0FA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#655dc6]" />
      </div>
    );
  }

  const displayName = language === 'en' && user?.name_en ? user.name_en : user?.name;

  return (
    <div className="min-h-screen bg-[#F2F0FA]" dir={direction}>
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] dark:border-slate-700 py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* User Info Card */}
        <Card className="border-[#C4BFF0]">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="relative group">
                <EmployeeAvatar
                  userId={user?.id}
                  name={displayName}
                  photoPath={user?.photo_path}
                  photoUpdatedAt={user?.photo_updated_at}
                  size={72}
                  fontClass="text-2xl"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  title={language === 'ar' ? 'تغيير الصورة' : 'Change photo'}
                  className="absolute -bottom-1 -end-1 h-7 w-7 rounded-full bg-white border-2 border-[#655dc6] text-[#655dc6] flex items-center justify-center shadow-sm hover:bg-[#655dc6] hover:text-white transition disabled:opacity-50"
                  data-testid="upload-photo-btn"
                >
                  {uploadingPhoto
                    ? <div className="animate-spin h-3 w-3 border-b-2 border-current rounded-full" />
                    : <Camera size={14} weight="bold" />}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoSelect}
                  data-testid="photo-file-input"
                />
              </div>
              <div className="flex-1 min-w-0">
                <CardTitle className="text-[#655dc6] break-words">{displayName}</CardTitle>
                <p className="text-sm text-[#655dc6]/60">{language === 'en' ? (user?.position_en || user?.position) : user?.position} - {getDeptLabel(user?.department, language)}</p>
                <p className="text-xs text-[#655dc6]/40">{user?.employee_number ? `#${user.employee_number}` : ''}</p>
                {user?.photo_path && (
                  <button
                    type="button"
                    onClick={handlePhotoDelete}
                    className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-red-600 hover:text-red-800"
                    data-testid="delete-photo-btn"
                  >
                    <Trash size={12} /> {language === 'ar' ? 'حذف الصورة' : 'Remove photo'}
                  </button>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Edit Profile */}
        <Card className="border-[#C4BFF0]" data-testid="edit-profile-card">
          <CardHeader>
            <CardTitle className="text-[#655dc6] flex items-center gap-2">
              <User size={22} weight="duotone" />
              {t('edit_profile')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileSave} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#655dc6]/80">{t('email')}</Label>
                  <div className="relative">
                    <EnvelopeSimple size={18} className="absolute start-3 top-1/2 -translate-y-1/2 text-[#655dc6]/50" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-[#E8E6F5] border-[#C4BFF0] ps-10"
                      data-testid="profile-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#655dc6]/80">{t('phone')}</Label>
                  <div className="relative">
                    <Phone size={18} className="absolute start-3 top-1/2 -translate-y-1/2 text-[#655dc6]/50" />
                    <Input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="bg-[#E8E6F5] border-[#C4BFF0] ps-10"
                      placeholder="+966 5XX XXX XXXX"
                      data-testid="profile-phone-input"
                    />
                  </div>
                </div>
              </div>
              <Button
                type="submit"
                disabled={savingProfile}
                className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2"
                data-testid="save-profile-btn"
              >
                {savingProfile ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <FloppyDisk size={18} weight="bold" />}
                {t('save')}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="border-[#C4BFF0]" data-testid="change-password-card">
          <CardHeader>
            <CardTitle className="text-[#655dc6] flex items-center gap-2">
              <Lock size={22} weight="duotone" />
              {t('change_password')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#655dc6]/80">{t('current_password')}</Label>
                <div className="relative">
                  <Input
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="bg-[#E8E6F5] border-[#C4BFF0] pe-10"
                    data-testid="current-password-input"
                  />
                  <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute end-3 top-1/2 -translate-y-1/2 text-[#655dc6]/50">
                    {showCurrent ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#655dc6]/80">{t('new_password')}</Label>
                  <div className="relative">
                    <Input
                      type={showNew ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-[#E8E6F5] border-[#C4BFF0] pe-10"
                      data-testid="new-password-input"
                    />
                    <button type="button" onClick={() => setShowNew(!showNew)} className="absolute end-3 top-1/2 -translate-y-1/2 text-[#655dc6]/50">
                      {showNew ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#655dc6]/80">{t('confirm_new_password')}</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-[#E8E6F5] border-[#C4BFF0]"
                    data-testid="confirm-new-password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword}
                className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2"
                data-testid="change-password-btn"
              >
                {savingPassword ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <Lock size={18} weight="bold" />}
                {t('change_password')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
